import {
  type ReactNode,
  createContext,
  memo,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef
} from 'react'
import { useEffectOnce } from 'react-use'
import dynamic from 'next/dynamic'
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  type UseSurveysCheckDisplayListResponse,
  type UseSurveysTriggersListResponse,
  useSurveysCheckDisplayList,
  useSurveysCloseCreate,
  useSurveysTriggersList
} from '@credit-web/services/surveys'
import { type SurveysCheckDisplayComponentType } from '@credit-web/services/types'
import { Modal, Spinner } from '@stone-payments/jade'

import {
  type SurveysInAppFeedbackProps,
  type SurveysInAppFeedbackPropsEvents,
  SurveysInAppFeedbackCard
} from '../components/feedback/surveys-in-app-feedback'
import { type TWebviewRuntime, SurveysQuestionTypeEnum } from '../entities'
import { type DecodedTriggerPayload, type SurveyTrigger } from '../entities/surveys/data-contracts'
import { mapApiComponentType, SurveysLogger } from '../utils'

type SurveysTriggersContent = UseSurveysTriggersListResponse['content']
type SurveysCheckDisplayContent = UseSurveysCheckDisplayListResponse['content']

// Survey State Management
interface SurveyState {
  isContextLoading: boolean
  isFeedbackPositive: boolean
  surveyAnswered: boolean
  modalOpen: boolean
  surveyDisplayDetails?: SurveysCheckDisplayContent
  selectedQuestionType: SurveysQuestionTypeEnum
  shouldFetchSurveyDetails: boolean
  hasSurveyAvailable: boolean
}

type SurveyAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_FEEDBACK_POSITIVE' }
  | { type: 'SET_SURVEY_ANSWERED'; payload: boolean }
  | { type: 'TOGGLE_MODAL'; payload?: boolean }
  | { type: 'SET_SURVEY_DETAILS'; payload: SurveysCheckDisplayContent }
  | { type: 'SET_QUESTION_TYPE'; payload: SurveysQuestionTypeEnum }
  | { type: 'SET_SHOULD_FETCH'; payload: boolean }
  | { type: 'OPEN_SURVEY'; payload: boolean }
  | { type: 'RESET_SURVEY_STATE' }
  | { type: 'EXIT_SURVEY' }

const initialSurveyState: SurveyState = {
  isContextLoading: false,
  isFeedbackPositive: false,
  surveyAnswered: false,
  modalOpen: false,
  surveyDisplayDetails: undefined,
  selectedQuestionType: SurveysQuestionTypeEnum.Unavailable,
  shouldFetchSurveyDetails: false,
  hasSurveyAvailable: false
}

const surveyReducer = (state: SurveyState, action: SurveyAction): SurveyState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isContextLoading: action.payload }

    case 'SET_FEEDBACK_POSITIVE':
      return { ...state, isFeedbackPositive: true }

    case 'SET_SURVEY_ANSWERED':
      return { ...state, surveyAnswered: action.payload }

    case 'TOGGLE_MODAL':
      return { ...state, modalOpen: action.payload ?? !state.modalOpen }

    case 'SET_SURVEY_DETAILS':
      return { ...state, surveyDisplayDetails: action.payload }

    case 'SET_QUESTION_TYPE':
      return { ...state, selectedQuestionType: action.payload }

    case 'SET_SHOULD_FETCH':
      return { ...state, shouldFetchSurveyDetails: action.payload }

    case 'OPEN_SURVEY':
      return {
        ...state,
        modalOpen: true,
        shouldFetchSurveyDetails: true,
        hasSurveyAvailable: action.payload
      }

    case 'RESET_SURVEY_STATE':
      return {
        ...state,
        surveyDisplayDetails: undefined,
        selectedQuestionType: SurveysQuestionTypeEnum.Unavailable,
        shouldFetchSurveyDetails: false
      }

    case 'EXIT_SURVEY':
      return {
        ...state,
        modalOpen: false,
        surveyAnswered: false,
        shouldFetchSurveyDetails: false,
        surveyDisplayDetails: undefined,
        selectedQuestionType: SurveysQuestionTypeEnum.Unavailable,
        isFeedbackPositive: false
      }

    default:
      return state
  }
}

interface SurveysContextType {
  document: string
  triggers?: SurveysTriggersContent
  surveyDisplayDetails?: SurveysCheckDisplayContent
  selectedQuestionType?: SurveysQuestionTypeEnum
  hasSurveyAvailable: boolean
  surveyKey: string | undefined | null
  invokeSurvey: (eventName: string, callback?: () => void) => Promise<void>
  registerWebviewFinishSurvey: (eventName: string, callback?: () => void) => void
  setFeedbackPositive: () => void
  setLoading: (next: boolean) => void
  setSurveyAnswered: (answered: boolean) => void
  exit: () => void
  refetch: () => void
}

const SurveysContext = createContext<SurveysContextType | undefined>(undefined)

interface SurveysTriggersProviderProps {
  children: ReactNode
  document: string
  webviewRuntime?: TWebviewRuntime
  initialTriggers?: DecodedTriggerPayload
}

export const BUCKET_KEY = 'credit::surveys::triggers-data'

export const SurveysInApp = dynamic(() => import('../surveys-in-app').then(mod => mod.SurveysInApp), {
  loading: () => <Spinner size="large" />
})

const Feedback = memo(({ feedback }: { feedback: SurveysInAppFeedbackProps & SurveysInAppFeedbackPropsEvents }) => {
  return <SurveysInAppFeedbackCard {...feedback} />
})

const Waiting = memo(() => {
  return (
    <div className="flex justify-center items-center content-center flex-col flex-1">
      <Spinner color="positive" size="medium" />
    </div>
  )
})

export const SurveysProvider: React.FC<SurveysTriggersProviderProps> = ({
  children,
  document,
  webviewRuntime,
  initialTriggers
}) => {
  const normalizedInitialTriggers = useMemo<SurveyTrigger[] | undefined>(() => {
    if (!initialTriggers) {
      return undefined
    }

    const { eventId, surveyKey } = initialTriggers
    if (!eventId && !surveyKey) {
      return undefined
    }

    return [
      {
        surveyId: undefined,
        surveyKey,
        eventsName: eventId ? [eventId] : []
      }
    ]
  }, [initialTriggers])

  const hasInitialTriggers = Array.isArray(normalizedInitialTriggers) && normalizedInitialTriggers.length > 0

  const [surveyState, dispatch] = useReducer(surveyReducer, initialSurveyState)
  const currentSurveyRef = useRef<SurveyTrigger | undefined>(undefined)
  const externalExitCallback = useRef<(() => void) | undefined>(undefined)
  const wasClosedByOverlayRef = useRef<boolean>(false)
  const surveyPromiseResolveRef = useRef<((value: void | PromiseLike<void>) => void) | undefined>(undefined)
  const surveyPromiseRejectRef = useRef<((reason?: any) => void) | undefined>(undefined)
  const shouldResolveOnModalCloseRef = useRef(false)

  const surveyKey: string | undefined | null = currentSurveyRef.current?.surveyKey
  const isWebView = webviewRuntime?.isWebView() || window?.isWebView ? window?.isWebView() : false
  const webviewFinishMethod = window?.webviewFinishMethod || webviewRuntime?.webviewFinishMethod

  let startTime: number = 0
  useEffectOnce(() => {
    startTime = Date.now()
  })

  const exit = useCallback(() => {
    SurveysLogger.info('Exit survey initiated', {
      label: 'survey-exit',
      isWebView,
      exitCallback: String(typeof externalExitCallback.current === 'function'),
      hasWebviewFinishMethod: typeof webviewFinishMethod === 'function',
      timestamp: new Date().toISOString()
    })

    dispatch({ type: 'EXIT_SURVEY' })

    // Resolve a promessa do invokeSurvey se existir
    if (!shouldResolveOnModalCloseRef.current) {
      if (surveyPromiseResolveRef.current) {
        surveyPromiseResolveRef.current()
        surveyPromiseResolveRef.current = undefined
        surveyPromiseRejectRef.current = undefined
      }

      if (typeof externalExitCallback.current === 'function') {
        externalExitCallback.current()
        externalExitCallback.current = undefined
      }
    }
  }, [isWebView, webviewFinishMethod])

  // Salva dados no CreditBucket - Desabilitado no V1
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
  /* const saveToBucket = (surveysData: SurveysTriggersContent) => {
    try {
      CreditBucket.set(BUCKET_KEY, surveysData)
    } catch (error) {
      console.warn('Erro ao salvar dados no CreditBucket:', error)
    }
  } */

  const {
    refetch,
    content: triggers,
    isLoading: isTriggersLoading,
    isError: isTriggersError
  } = useSurveysTriggersList(
    { Document: document },
    {
      enabled: !hasInitialTriggers,
      retry: 3, // Reduzindo tentativas
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Backoff exponencial
      staleTime: 5 * 60 * 1000, // 5 minutos - considera dados frescos
      cacheTime: 10 * 60 * 1000 // 10 minutos - mantém dados em cache
    }
  )

  const activeTriggers = hasInitialTriggers ? normalizedInitialTriggers : triggers
  const shouldWaitForTriggers = !hasInitialTriggers && isTriggersLoading

  useEffect(() => {
    if (activeTriggers) {
      SurveysLogger.info(hasInitialTriggers ? 'Triggers loaded from payload' : 'Triggers loaded from API', {
        label: hasInitialTriggers ? 'triggers-loaded-payload' : 'triggers-loaded-api',
        triggersCount: activeTriggers.length,
        triggers: JSON.stringify(activeTriggers, null, 2),
        timestamp: new Date().toISOString()
      })
    }
  }, [activeTriggers, hasInitialTriggers])

  const surveyCloseMutation = useSurveysCloseCreate()

  const {
    content: surveyDetailsResponse,
    isError,
    isFetching,
    isLoading: isSurveyDetailLoading
  } = useSurveysCheckDisplayList(
    { key: currentSurveyRef.current?.surveyKey, Document: document },
    {
      enabled: surveyState.shouldFetchSurveyDetails && Boolean(currentSurveyRef.current?.surveyKey),
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 15000), // Backoff
      staleTime: 2 * 60 * 1000, // 2 minutos
      cacheTime: 5 * 60 * 1000 // 5 minutos
    }
  )

  const handleFeedbackAction = useCallback(() => {
    SurveysLogger.info('Feedback action triggered', {
      label: 'feedback-action',
      feedbackType: 'positive',
      timestamp: new Date().toISOString()
    })

    if (surveyDetailsResponse) {
      const endTime = Date.now()
      const timeSpent = endTime - startTime

      SurveysLogger.info('Survey question view tracked', {
        label: 'survey-view',
        survey: {
          id: surveyDetailsResponse?.surveyDetails?.surveyId,
          timeSpentMs: timeSpent,
          timeSpentSeconds: Math.round(timeSpent / 1000)
        }
      })
    }

    exit()
  }, [exit, startTime, surveyDetailsResponse])

  useEffect(() => {
    if (isError && !isFetching) {
      SurveysLogger.warn('Survey key loading failed', {
        label: 'survey-key-load-error',
        surveyKey,
        isError,
        isFetching,
        timestamp: new Date().toISOString(),
        context: SurveysLogger.getWebviewContext()
      })
    }
  }, [isError, isFetching, surveyKey, webviewRuntime])

  // Timeout customizado para survey details
  useEffect(() => {
    if (!surveyState.shouldFetchSurveyDetails || !isSurveyDetailLoading) {
      return
    }

    const timeoutId = setTimeout(() => {
      if (isSurveyDetailLoading) {
        SurveysLogger.warn('Survey details request timed out - calling exit', {
          label: 'survey-details-timeout',
          surveyKey,
          timeout: '30s',
          timestamp: new Date().toISOString()
        })

        // Chama exit automaticamente quando há timeout
        exit()
      }
    }, 30000) // 30 segundos de timeout

    return () => clearTimeout(timeoutId)
  }, [surveyState.shouldFetchSurveyDetails, isSurveyDetailLoading, surveyKey, exit])

  useEffect(() => {
    if (isSurveyDetailLoading || isFetching || !surveyState.shouldFetchSurveyDetails) return

    if (surveyDetailsResponse) {
      if (surveyDetailsResponse && surveyDetailsResponse?.surveyDetails?.questions?.length === 0) {
        SurveysLogger.warn('Survey has no questions', {
          label: 'no-questions-found',
          surveyKey,
          surveyId: surveyDetailsResponse?.surveyDetails?.surveyId,
          questionsLength: surveyDetailsResponse?.surveyDetails?.questions.length,
          timestamp: new Date().toISOString()
        })
        return
      }

      const componentTypeMapped = mapApiComponentType(
        surveyDetailsResponse?.surveyDetails?.questions?.[0].componentType as SurveysCheckDisplayComponentType
      )
      if (componentTypeMapped !== SurveysQuestionTypeEnum.Unavailable) {
        dispatch({ type: 'SET_QUESTION_TYPE', payload: componentTypeMapped })
      } else {
        SurveysLogger.warn('Unsupported component type detected', {
          label: 'unsupported-component-type',
          surveyKey,
          surveyId: surveyDetailsResponse?.surveyDetails?.surveyId,
          componentType: surveyDetailsResponse?.surveyDetails?.questions?.[0].componentType,
          mappedType: componentTypeMapped,
          timestamp: new Date().toISOString()
        })
        return
      }

      dispatch({ type: 'SET_SURVEY_DETAILS', payload: surveyDetailsResponse })
    }
  }, [surveyDetailsResponse, surveyKey, isSurveyDetailLoading, isFetching, surveyState.shouldFetchSurveyDetails])

  const internalSetFeedbackPositive = useCallback(() => {
    dispatch({ type: 'SET_FEEDBACK_POSITIVE' })
  }, [])

  const internalSetLoading = useCallback((next: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: next })
  }, [])

  const internalSetSurveyAnswered = useCallback((answered: boolean) => {
    dispatch({ type: 'SET_SURVEY_ANSWERED', payload: answered })
  }, [])

  const handleModalClose = useCallback(() => {
    // Se o usuário não respondeu a pesquisa, tem uma survey key válida E não fechou pelo overlay, chama o mutation de close
    // O fechamento de uma pesquisa sem resposta deve ser por uma escolha explicita pelo clique no X
    if (!surveyState.surveyAnswered && surveyKey && !wasClosedByOverlayRef.current) {
      SurveysLogger.info('Survey closed without answered', {
        label: 'survey-closed-no-answered',
        surveyKey,
        timestamp: new Date().toISOString()
      })

      surveyCloseMutation.mutate({
        surveyKey,
        query: { Document: document }
      })
    }

    // Reset do estado de fetch quando o modal for fechado
    dispatch({ type: 'SET_SHOULD_FETCH', payload: false })

    // Reset da flag do overlay
    wasClosedByOverlayRef.current = false

    exit()

    shouldResolveOnModalCloseRef.current = false
    if (surveyPromiseResolveRef.current) {
      surveyPromiseResolveRef.current()
      surveyPromiseResolveRef.current = undefined
      surveyPromiseRejectRef.current = undefined
    }

    if (typeof externalExitCallback.current === 'function') {
      externalExitCallback.current()
      externalExitCallback.current = undefined
    }
  }, [surveyState.surveyAnswered, surveyKey, exit, surveyCloseMutation, document])

  // Effect para detectar cliques no overlay do modal
  useEffect(() => {
    if (!surveyState.modalOpen) {
      return
    }

    const handleOverlayClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (target && target.classList.contains('jade-floating__overlay')) {
        SurveysLogger.info('Survey closed by overlay click', {
          label: 'survey-closed-overlay',
          surveyKey,
          timestamp: new Date().toISOString()
        })
        wasClosedByOverlayRef.current = true
      }
    }

    // Adiciona o listener ao documento global
    globalThis.document?.addEventListener('click', handleOverlayClick, true)

    // Cleanup - remove o listener quando o modal for fechado ou o componente desmontado
    return () => {
      globalThis.document?.removeEventListener('click', handleOverlayClick, true)
    }
  }, [surveyState.modalOpen, surveyKey])

  const invokeSurvey = useCallback(
    (eventName: string, callback?: () => void, autoExit = true): Promise<void> => {
      if (surveyPromiseResolveRef.current) {
        surveyPromiseResolveRef.current()
      }
      shouldResolveOnModalCloseRef.current = false

      return new Promise<void>((resolve, reject) => {
        // Armazena as funções de resolve/reject da promessa
        surveyPromiseResolveRef.current = resolve
        surveyPromiseRejectRef.current = reject

        if (typeof callback === 'function') {
          externalExitCallback.current = callback
        }

        // Se ainda está carregando, aguarda um pouco e tenta novamente
        if (shouldWaitForTriggers) {
          setTimeout(() => {
            invokeSurvey(eventName, callback, autoExit).then(resolve).catch(reject)
          }, 500)
          return
        }

        // Se há erro, resolve imediatamente
        if (isTriggersError) {
          resolve()
          return
        }

        if (activeTriggers && typeof eventName === 'string') {
          const surveysWithEvent = activeTriggers.filter((survey: SurveyTrigger) => {
            const events = (survey as any)?.eventsName || (survey as any)?.events_name || []
            return Array.isArray(events) && events.includes(eventName)
          })

          const hasEvent = surveysWithEvent.length > 0

          if (hasEvent) {
            currentSurveyRef.current = surveysWithEvent[0]
            const hasSurveyAvailable = Boolean(surveysWithEvent[0].surveyKey && hasEvent)

            if (hasSurveyAvailable) {
              dispatch({ type: 'OPEN_SURVEY', payload: hasSurveyAvailable })
              shouldResolveOnModalCloseRef.current = true
              return
            }
          }
        }

        if (autoExit) {
          exit()
        } else {
          resolve()
        }
      })
    },
    [exit, activeTriggers, shouldWaitForTriggers, isTriggersError]
  )

  const toggleModal = useCallback((value?: boolean) => {
    dispatch({ type: 'TOGGLE_MODAL', payload: value })
  }, [])

  const registerWebviewFinishSurvey = useCallback(
    (eventName: string, callback?: () => void) => {
      const registerFinishInterceptor = window?.registerFinishInterceptor || webviewRuntime?.registerFinishInterceptor

      if (registerFinishInterceptor) {
        registerFinishInterceptor(async () => {
          await invokeSurvey(eventName, callback)
        })
      }
    },
    [webviewRuntime, invokeSurvey]
  )

  const contextValue: SurveysContextType = {
    document,
    triggers: activeTriggers,
    hasSurveyAvailable: surveyState.hasSurveyAvailable,
    surveyDisplayDetails: surveyState.surveyDisplayDetails,
    selectedQuestionType: surveyState.selectedQuestionType,
    surveyKey,
    setFeedbackPositive: internalSetFeedbackPositive,
    invokeSurvey,
    registerWebviewFinishSurvey,
    setLoading: internalSetLoading,
    setSurveyAnswered: internalSetSurveyAnswered,
    refetch,
    exit
  }

  return (
    <SurveysContext.Provider value={contextValue}>
      {children}
      {Array.isArray(activeTriggers) && activeTriggers?.length > 0 && surveyState.modalOpen && (
        <Modal
          open={surveyState.modalOpen}
          toggle={toggleModal}
          closeOn={['esc', 'outside']}
          onClose={handleModalClose}
          title={surveyState?.surveyDisplayDetails?.surveyDetails?.title || ''}
        >
          <Suspense fallback={<Waiting />}>
            {surveyState.isFeedbackPositive && (
              <Feedback
                feedback={{
                  action: handleFeedbackAction
                }}
              />
            )}
            {!surveyState.isFeedbackPositive &&
              (surveyState.isContextLoading || isSurveyDetailLoading || !surveyState.surveyDisplayDetails ? (
                <Waiting />
              ) : (
                <SurveysInApp />
              ))}
          </Suspense>
        </Modal>
      )}
    </SurveysContext.Provider>
  )
}

/**
 * Hook para acessar os dados de surveys triggers
 * Deve ser usado dentro de um componente que está envolvido pelo SurveysTriggersProvider
 */
export const useSurveys = () => {
  const context = useContext(SurveysContext)
  if (!context) {
    throw new Error('useSurveys must be used within a SurveysTriggersProvider')
  }
  return context
}
